import { FC, useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { TOKENS, ConfirmDialog } from '../../components/design-system';
import {
  linkExistingUserToCompany,
  UserLookupResult,
  ExistingUserRoleKind,
} from './api';

interface Props {
  lookup: {
    loading: boolean;
    result: UserLookupResult | null;
    error: string | null;
  };
  role: ExistingUserRoleKind;
  companyId: number | undefined;
  /** Chamado quando o vínculo foi feito com sucesso — normalmente pra
   *  fechar o modal e refetch a lista. */
  onLinked: () => void;
}

const ROLE_LABEL: Record<ExistingUserRoleKind, string> = {
  operator: 'Operador',
  coordinator: 'Coordenador',
  manager: 'Gerente',
  administrator: 'Administrador',
};

/**
 * Renderiza um painel abaixo do campo email conforme o resultado do
 * lookup. Estados possíveis:
 *
 *  - loading   → spinner discreto
 *  - error     → nada (falha silenciosa; user segue no fluxo normal)
 *  - not found → nada (deixa o form salvar normalmente)
 *  - found & já pertence à empresa → alerta bloqueando com aviso
 *  - found & disponível pra vincular → card com botão "Vincular como X"
 */
export const ExistingUserPanel: FC<Props> = ({ lookup, role, companyId, onLinked }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [linking, setLinking] = useState(false);

  if (lookup.loading) {
    return (
      <div style={infoBox}>
        <Loader2 size={14} className='spin' color={TOKENS.color.muted} />
        <span style={{ fontSize: 12, color: TOKENS.color.muted }}>Verificando cadastro…</span>
      </div>
    );
  }
  if (!lookup.result?.found) return null;

  const user = lookup.result.user!;
  const already = lookup.result.already_in_company;
  const label = ROLE_LABEL[role];

  // Blocked (status_id=4) — cadastro suspenso pelo admin do sistema.
  // Não permitir vincular a novas empresas até que seja reativado.
  if (user.status === 'blocked') {
    return (
      <div style={{ ...alertBox, background: TOKENS.color.severity.high.bg, borderColor: TOKENS.color.severity.high.text }}>
        <AlertCircle size={16} color={TOKENS.color.severity.high.text} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: TOKENS.color.text, lineHeight: 1.5 }}>
          <strong>{user.name}</strong> tem cadastro <strong>bloqueado</strong> no sistema.
          Contate o administrador da plataforma para reativar antes de vincular a esta empresa.
        </div>
      </div>
    );
  }

  // Inactive (status_id=5) — cadastro inativo, precisa ser reativado
  // (mudar status pra Ativo) na tela onde ele já pertence.
  if (user.status === 'inactive') {
    return (
      <div style={{ ...alertBox, background: TOKENS.color.severity.medium.bg, borderColor: TOKENS.color.severity.medium.text }}>
        <AlertCircle size={16} color={TOKENS.color.severity.medium.text} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: TOKENS.color.text, lineHeight: 1.5 }}>
          <strong>{user.name}</strong> tem cadastro <strong>inativo</strong>.
          Ele precisa ser reativado (status Ativo) antes de poder ser vinculado a esta empresa.
        </div>
      </div>
    );
  }

  if (already) {
    return (
      <div style={{ ...alertBox, background: TOKENS.color.severity.medium.bg, borderColor: TOKENS.color.severity.medium.text }}>
        <AlertCircle size={16} color={TOKENS.color.severity.medium.text} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: TOKENS.color.text, lineHeight: 1.5 }}>
          <strong>{user.name}</strong> já está cadastrado nesta empresa como{' '}
          <strong>{already.role_name}</strong>. Não é possível cadastrar novamente com o mesmo email.
          Se precisar mudar o perfil, edite o cadastro existente na lista.
        </div>
      </div>
    );
  }

  const doLink = async () => {
    if (!companyId) return;
    setLinking(true);
    try {
      await linkExistingUserToCompany(role, user.id, companyId);
      toast.success(`${user.name} vinculado como ${label}.`);
      setConfirmOpen(false);
      onLinked();
    } catch (e: any) {
      toast.error(`Erro ao vincular: ${e?.response?.data?.message ?? e?.message ?? 'desconhecido'}`);
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <div style={{ ...alertBox, background: TOKENS.color.severity.low.bg, borderColor: TOKENS.color.severity.low.text }}>
        <CheckCircle2 size={16} color={TOKENS.color.severity.low.text} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
          <div style={{ fontSize: 12, color: TOKENS.color.text, lineHeight: 1.5 }}>
            <strong>{user.name}</strong> já tem cadastro no sistema (doc {user.document_number}).
            Deseja vincular este usuário à empresa como <strong>{label}</strong>?
          </div>
          <div>
            <button
              type='button'
              onClick={() => setConfirmOpen(true)}
              style={{
                background: TOKENS.color.action, color: '#fff', border: 'none',
                padding: '6px 14px', fontSize: 12, fontWeight: 700,
                borderRadius: TOKENS.radius.chip, cursor: 'pointer',
              }}
            >
              Vincular como {label}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title={`Vincular ${user.name}?`}
        description={
          <>
            <strong>{user.name}</strong> ({user.email}) será vinculado a esta empresa como{' '}
            <strong>{label}</strong>. Nenhum dado do usuário existente é alterado.
          </>
        }
        confirmLabel={`Vincular como ${label}`}
        tone='action'
        loading={linking}
        onCancel={() => { if (!linking) setConfirmOpen(false); }}
        onConfirm={doLink}
      />
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

const infoBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '6px 10px', marginTop: 6,
  background: TOKENS.color.surface,
  borderRadius: TOKENS.radius.chip,
};
const alertBox: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8,
  padding: 10, marginTop: 8,
  border: '1px solid',
  borderRadius: TOKENS.radius.card,
};
